<template>
    <div class="dropdown" :class="{'is-active': isActive, 'is-mobile': showMobileStyle}" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
        <div class="dropdown__activator cursor-pointer" @click.stop.prevent="handleClick">
            <slot/>
        </div>

		<!-- Client-only required due to Teleport -->
        <client-only>
			<slide-sheet v-if="showMobileStyle" v-model="isActive" :persistent-on-same="persistentOnSame" :key="menuKey" :plain="plainMenu" :title="menuTitle" :fixed-height="fixMobileHeight">
				<template #header>
					<slot name="title" v-bind:isMobile="showMobileStyle"/>
				</template>
				<template #default>
					<template v-for="(action, actionIndex) in actions" :key="actionIndex">
						<div v-if="action.divider" class="px-16">
							<divider :margin="8"/>
						</div>
						<slide-sheet-action v-else v-bind="action" @click="handleClickedAction"/>
					</template>
					<div v-if="$slots.menu" @click="handleMenuClick">
						<slot name="menu" v-bind:isMobile="showMobileStyle"/>
					</div>
				</template>
				<template #footer>
					<slot name="footer" v-bind:isMobile="showMobileStyle"/>
				</template>
			</slide-sheet>
			<transition v-else-if="hover" :name="transition">
				<div v-show="isActive" ref="popup" :style="menuStyle">
					<div class="dropdown__menu" :class="{'py-8': !plainMenu}"
						 v-click-outside="{handler: hide, capture: !persistentOnSame, ignore: ['.popup-alert', '.form-date-input-picker']}">
						<div v-if="menuTitle" class="font-bold text-sm px-16 my-8">{{ menuTitle }}</div>
						<slot name="title" v-bind:isMobile="showMobileStyle"/>

						<box :max-height="maxMenuHeight" :persist-height="popupPlacement === 'top'" @click="handleMenuClick">
							<nav-list v-if="actions.length">
								<template v-for="(action, actionIndex) in actions" :key="actionIndex">
									<divider v-if="action.divider" :margin="8"/>
									<nav-list-item v-else :to="action.to" :text="action.name" :color="action.color" @click="action.callback">
										<cdn-image v-if="action.suffix_image" :src="action.suffix_image.src" :size="{height: 20, ratio: 1}" :alt="action.name" :rounded-full="action.suffix_image.rounded"/>
									</nav-list-item>
								</template>

							</nav-list>

							<slot name="menu" v-bind:isMobile="showMobileStyle"/>
						</box>

						<slot name="footer" v-bind:isMobile="showMobileStyle"/>

					</div>
				</div>
			</transition>
			<Teleport v-else to=".dropdown-portal" :key="menuKey">
				<transition :name="transition">
					<div v-if="isActive" ref="popup" :style="menuStyle">
						<div class="dropdown__menu" :class="{'py-8': !plainMenu}"
							 v-click-outside="{handler: hide, capture: !persistentOnSame, ignore: ['.popup-alert', '.form-date-input-picker']}">
							<div v-if="menuTitle" class="font-bold text-sm px-16 my-8">{{ menuTitle }}</div>
							<slot name="title" v-bind:isMobile="showMobileStyle"/>

							<box :max-height="maxMenuHeight" :persist-height="popupPlacement === 'top'" @click="handleMenuClick">
								<nav-list v-if="actions.length">
									<template v-for="(action, actionIndex) in actions" :key="actionIndex">
										<divider v-if="action.divider" :margin="8"/>
										<nav-list-item v-else :to="action.to" :text="action.name" :color="action.color" @click="action.callback">
											<cdn-image v-if="action.suffix_image" :src="action.suffix_image.src" :size="{height: 20, ratio: 1}" :alt="action.name" :rounded-full="action.suffix_image.rounded"/>
										</nav-list-item>
									</template>
								</nav-list>

								<slot name="menu" v-bind:isMobile="showMobileStyle"/>
							</box>

							<slot name="footer" v-bind:isMobile="showMobileStyle"/>
						</div>
					</div>
				</transition>
			</Teleport>
		</client-only>
    </div>
</template>

<script>
import RelativePopupElement from "../../mixins/RelativePopupElement";
import {formatDimension} from "@spoferan/spoferan-ts-core";
import {disableWindowScroll, enableWindowScroll, shakeElement} from "@spoferan/spoferan-ts-core";

export default {
    name: "Dropdown",

    mixins: [RelativePopupElement],

    props: {

        // A title to display above the menu content
        menuTitle: {
            type: String,
            default: null
        },

        // A component key for the dropdown menu as sometimes required to fix render issues
        menuKey: {
            type: String,
            default: null
        },

        // Whether to not add any padding on the menu
        plainMenu: {
            type: Boolean,
            default: false
        },

        fixMobileHeight: {
			type: Boolean,
			default: false
        },

        // Whether to not trigger a hide within the dropdown
        disableHide: {
            type: Boolean,
            default: false
        },

        // Whether the toggle of the dropdown is disabled
        disabled: {
            type: Boolean,
            default: false
        },

        // Whether to open the dropdown on hover
        hover: {
            type: Boolean,
            default: false
        },

        // Whether the dropdown stays active when another dropdown is clicked
        // This is currently required to support nested dropdowns, e.g., on InlineFormInput components with FormSelect
        persistentOnSame: {
            type: Boolean,
            default: false
        },

		actions: {
			type: Array,
			default: () => []
		}
    },

    data() {
        return {
            lastActiveChangeDueClick: 0 // Required to prevent double toggle on fast double clicks
        }
    },

    computed: {
        hasRelativePosition() {
            return this.hover;
        },

        transition() {
            if (this.popupPlacement === 'top') {
                return 'dropdown-top-transition'
            }

            return 'dropdown-bottom-transition'
        },

        maxMenuHeight() {
            if (this.maxHeight) {
                return this.maxHeight;
            }
            if (this.popupPlacement === 'top') {
                return this.activatorDimensions.distance_to_top_window - 8;
            }
            if (this.popupPlacement === 'bottom') {
                return this.activatorDimensions.distance_to_bottom_window - 8;
            }

            return null;
        },

        showMobileStyle() {
            return (this.$device.isMobile || this.$device.isTablet);
        },

        menuStyle() {
            if (this.showMobileStyle) {
                return {}
            }

            return this.popupStyle
        }
    },

    watch: {
        isActive(active) {
            this.lastActiveChangeDueClick = Date.now();
            if (this.showMobileStyle && active) {
                disableWindowScroll();
            } else if (!active) {
                enableWindowScroll();
            }
        },

        '$route.path'() {
            this.isActive = false
        }
    },

    beforeUnmount() {
        this.isActive = false;
        enableWindowScroll();
    },

    methods: {
        shallUpdatePopupDimensions() {
            return this.isActive && !this.showMobileStyle;
        },

        handleClick() {
            if (!this.hover) {
                this.toggle();
            } else {
                this.hide();
            }
        },

        handleMouseEnter() {
            if (this.hover) {
                this.show();
            }
        },

        handleMouseLeave() {
            if (this.hover) {
                this.hide();
            }
        },

        toggle() {
            if (!this.disabled && (this.hover || this.lastActiveChangeDueClick < (Date.now() - 200))) {
                this.isActive = !this.isActive;
            }
        },

        show() {
            if (!this.disabled && (this.hover || this.lastActiveChangeDueClick < (Date.now() - 200))) {
                this.isActive = true;
            }
        },

        hide() {
            if (!this.disableHide && (this.hover || this.lastActiveChangeDueClick < (Date.now() - 200))) {
                this.isActive = false;
            }
        },

        shakeMenu() {
            shakeElement(this.$refs.popup);
        },

		/**
		 * @deprecated remove when slots are migrated to actions
		 */
        handleMenuClick(event) {

            // Close the dropdown when clicked on a menu nav list item
            let target = event.target;
            let isClickableMenuTarget = false;
            while (target && !isClickableMenuTarget) {
                isClickableMenuTarget = target.className && target.className.split && target.className.split(' ').includes('nav-list-item');
                target = target.parentNode;
            }

            if (isClickableMenuTarget) {
                this.hide();
            }
        },

		handleClickedAction() {
			this.hide();
		}
    }
}
</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.dropdown {
  position: relative;
}

.dropdown__menu {
  display: block;
  box-sizing: border-box;
  border-radius: 4px;
  overflow-y: auto;
  overflow-x: hidden;
  contain: content;
  will-change: transform;
  background-color: #ffffff;
  box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.1);
}

.dropdown-bottom-transition-enter-active {
  animation: dropdownFadeInToBottom 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.dropdown-bottom-transition-leave-active {
  animation: dropdownFadeOutFromBottom 0.2s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}

.dropdown-top-transition-enter-active {
  animation: dropdownFadeInToTop 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.dropdown-top-transition-leave-active {
  animation: dropdownFadeOutFromTop 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}

@keyframes dropdownFadeInToBottom {
  0% {
    transform: translateY(-2em);
    opacity: 0;
  }
  100% {
    transform: translateY(0px);
    opacity: 1;
  }
}
@keyframes dropdownFadeOutFromBottom {
  0% {
    transform: translateY(0px);
    opacity: 1;
  }
  100% {
    transform: translateY(-8px);
    opacity: 0;
  }
}
@keyframes dropdownFadeInToTop {
  0% {
    transform: translateY(2em);
    opacity: 0;
  }
  100% {
    transform: translateY(0px);
    opacity: 1;
  }
}
@keyframes dropdownFadeOutFromTop {
  0% {
    transform: translateY(0px);
    opacity: 1;
  }
  100% {
    transform: translateY(2em);
    opacity: 0;
  }
}
</style>